import { useState, useRef, useEffect, useCallback } from 'react';
import { useBreakpoint } from '../../hooks/useBreakpoint';

// ---------------------------------------------------------------------------
// Utility Functions (pure, exported for testing)
// ---------------------------------------------------------------------------

/**
 * Normalizes an angle to the range [0, 359].
 */
export function normalizeAngle(angle) {
  const mod = ((angle % 360) + 360) % 360;
  return mod === 360 ? 0 : mod;
}

/**
 * Calculates clockwise angle (0° = up/north) from a circle center to a pointer position.
 */
export function angleFromPointerOnCircle(cx, cy, px, py) {
  const dx = px - cx;
  const dy = py - cy;
  // atan2(dx, -dy) gives clockwise angle from top (north)
  const rad = Math.atan2(dx, -dy);
  const deg = (rad * 180) / Math.PI;
  return normalizeAngle(deg);
}

/**
 * Snaps an angle to the nearest multiple of `step` degrees.
 */
export function snapAngle(angle, step = 5) {
  return normalizeAngle(Math.round(angle / step) * step);
}

/**
 * Returns the cardinal/intercardinal label for an angle (16 directions).
 * Uses German abbreviations: N, NNO, NO, ONO, O, OSO, SO, SSO, S, SSW, SW, WSW, W, WNW, NW, NNW
 */
export function angleToCardinalLabel(angle) {
  const directions = [
    'N', 'NNO', 'NO', 'ONO',
    'O', 'OSO', 'SO', 'SSO',
    'S', 'SSW', 'SW', 'WSW',
    'W', 'WNW', 'NW', 'NNW',
  ];
  const idx = Math.round(normalizeAngle(angle) / 22.5) % 16;
  return directions[idx];
}

// ---------------------------------------------------------------------------
// CompassWidget Component
// ---------------------------------------------------------------------------

const QUICK_DIRECTIONS = [
  { label: 'N', angle: 0 },
  { label: 'NO', angle: 45 },
  { label: 'O', angle: 90 },
  { label: 'SO', angle: 135 },
  { label: 'S', angle: 180 },
  { label: 'SW', angle: 225 },
  { label: 'W', angle: 270 },
  { label: 'NW', angle: 315 },
];

const DIAL_SIZE = 128;
const DIAL_RADIUS = 44;
const DIAL_CENTER = DIAL_SIZE / 2;

/**
 * CompassWidget — interactive compass for setting the garden's north direction.
 *
 * Props:
 *   northAngle {number|null} — degrees clockwise from canvas-top to magnetic north
 *   onNorthAngleChange {function} — called with new angle (number)
 *   northViewActive {boolean} — whether "Norden oben" view is active
 *   onNorthViewToggle {function} — called to toggle north-view
 */
const CompassWidget = ({
  northAngle,
  onNorthAngleChange,
  northViewActive,
  onNorthViewToggle,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(northAngle != null ? String(northAngle) : '');
  const [prevNorthAngle, setPrevNorthAngle] = useState(northAngle);
  const popoverRef = useRef(null);
  const widgetRef = useRef(null);

  // Position: am Desktop rechts neben dem Properties-Panel (300 px + 16 px);
  // Tablet hat kein Panel, Mobile zusätzlich nur den 44-px-Sub-Header —
  // die alte Fixed-Position (right: 316px) landete dort oben links im Bild.
  const { isMobile, isDesktop } = useBreakpoint();
  const widgetTop = isMobile ? '56px' : '176px';
  const widgetRight = isDesktop ? '316px' : '16px';

  // Keep input in sync with northAngle — adjusted during render rather than in an
  // effect to avoid a setState-in-effect cascade.
  if (northAngle !== prevNorthAngle) {
    setPrevNorthAngle(northAngle);
    setInputValue(northAngle != null ? String(northAngle) : '');
  }

  // Close popover on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        widgetRef.current && !widgetRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleDialClick = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const angle = snapAngle(angleFromPointerOnCircle(cx, cy, e.clientX, e.clientY));
    onNorthAngleChange(angle);
  }, [onNorthAngleChange]);

  const handleInputBlur = () => {
    const val = parseInt(inputValue, 10);
    if (!isNaN(val)) {
      onNorthAngleChange(normalizeAngle(val));
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      const val = parseInt(inputValue, 10);
      if (!isNaN(val)) {
        onNorthAngleChange(normalizeAngle(val));
      }
    }
  };

  // Needle tip position on the dial
  const needleRad = northAngle != null ? (northAngle * Math.PI) / 180 : 0;
  const needleTipX = DIAL_CENTER + DIAL_RADIUS * Math.sin(needleRad);
  const needleTipY = DIAL_CENTER - DIAL_RADIUS * Math.cos(needleRad);

  // Quick direction dot positions (on the outer ring)
  const outerRadius = DIAL_RADIUS + 14;
  const quickDotPositions = QUICK_DIRECTIONS.map(({ label, angle }) => {
    const rad = (angle * Math.PI) / 180;
    return {
      label,
      angle,
      x: DIAL_CENTER + outerRadius * Math.sin(rad),
      y: DIAL_CENTER - outerRadius * Math.cos(rad),
    };
  });

  return (
    <div style={{ position: 'fixed', top: widgetTop, right: widgetRight, zIndex: 10 }}>
      {/* Compact widget button */}
      <button
        ref={widgetRef}
        onClick={() => setIsOpen((v) => !v)}
        style={{
          width: '48px',
          height: '48px',
          backgroundColor: 'white',
          border: `1px solid ${isOpen ? '#10B981' : '#D1D5DB'}`,
          borderRadius: '4px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          cursor: 'pointer',
          padding: 0,
        }}
        title="Nordrichtung einstellen"
        aria-label="Kompass — Nordrichtung einstellen"
        aria-expanded={isOpen}
      >
        <svg
          width="36"
          height="36"
          viewBox="-18 -18 36 36"
          style={{
            transform: northAngle != null ? `rotate(${northAngle}deg)` : 'none',
            transition: 'transform 0.2s ease',
          }}
          aria-hidden="true"
          data-testid="compass-svg"
        >
          <circle cx="0" cy="0" r="16" fill="none" stroke="#E5E7EB" strokeWidth="1.5" />
          {/* Cardinal labels */}
          <text x="0" y="-9" textAnchor="middle" dominantBaseline="middle" fontSize="7" fontWeight="bold"
            fill={northAngle != null ? '#10B981' : '#374151'} data-testid="compass-n-label">
            {northAngle != null ? 'N' : '?'}
          </text>
          <text x="0" y="10" textAnchor="middle" dominantBaseline="middle" fontSize="5.5" fill="#9CA3AF">S</text>
          <text x="10" y="0" textAnchor="middle" dominantBaseline="middle" fontSize="5.5" fill="#9CA3AF">O</text>
          <text x="-10" y="0" textAnchor="middle" dominantBaseline="middle" fontSize="5.5" fill="#9CA3AF">W</text>
          {/* North needle (when angle is set) */}
          {northAngle != null ? (
            <line x1="0" y1="1" x2="0" y2="-14" stroke="#10B981" strokeWidth="2" strokeLinecap="round" />
          ) : (
            <line x1="0" y1="-14" x2="0" y2="14" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" />
          )}
        </svg>
      </button>

      {/* Popover */}
      {isOpen && (
        <div
          ref={popoverRef}
          style={{
            position: 'absolute',
            top: '56px',
            right: '0',
            backgroundColor: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            padding: '16px',
            width: '212px',
            zIndex: 11,
          }}
          data-testid="compass-popover"
        >
          <p style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: 600, color: '#111827' }}>
            Nordrichtung einstellen
          </p>

          {/* Dial */}
          <svg
            width={DIAL_SIZE}
            height={DIAL_SIZE}
            style={{ display: 'block', margin: '0 auto 12px', cursor: 'crosshair' }}
            onClick={handleDialClick}
            aria-label="Kompass-Dial — klicken um Nordrichtung zu setzen"
            data-testid="compass-dial"
          >
            {/* Background circle */}
            <circle cx={DIAL_CENTER} cy={DIAL_CENTER} r={DIAL_RADIUS + 18} fill="#F9FAFB" stroke="#E5E7EB" strokeWidth="1" />
            {/* Inner ring */}
            <circle cx={DIAL_CENTER} cy={DIAL_CENTER} r={DIAL_RADIUS} fill="none" stroke="#D1D5DB" strokeWidth="1" />
            {/* Cardinal/intercardinal labels */}
            {quickDotPositions.map(({ label, angle, x, y }) => {
              const isCardinal = ['N', 'O', 'S', 'W'].includes(label);
              return (
                <text
                  key={label}
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={isCardinal ? '9' : '7'}
                  fontWeight={isCardinal ? 'bold' : 'normal'}
                  fill={northAngle === angle ? '#10B981' : '#6B7280'}
                >
                  {label}
                </text>
              );
            })}
            {/* Center dot */}
            <circle cx={DIAL_CENTER} cy={DIAL_CENTER} r="3" fill="#374151" />
            {/* North needle */}
            {northAngle != null ? (
              <>
                <line
                  x1={DIAL_CENTER}
                  y1={DIAL_CENTER}
                  x2={needleTipX}
                  y2={needleTipY}
                  stroke="#10B981"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <circle cx={needleTipX} cy={needleTipY} r="4" fill="#10B981" />
              </>
            ) : (
              <text
                x={DIAL_CENTER}
                y={DIAL_CENTER}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="14"
                fill="#9CA3AF"
              >
                ?
              </text>
            )}
          </svg>

          {/* Number input row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
            <label
              htmlFor="compass-angle-input"
              style={{ fontSize: '12px', color: '#6B7280', flexShrink: 0 }}
            >
              Winkel:
            </label>
            <input
              id="compass-angle-input"
              type="number"
              min="0"
              max="359"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              placeholder="0–359"
              style={{
                width: '64px',
                border: '1px solid #D1D5DB',
                borderRadius: '4px',
                padding: '4px 6px',
                fontSize: '13px',
                color: '#111827',
              }}
              aria-label="Nordwinkel in Grad"
              data-testid="compass-angle-input"
            />
            <span style={{ fontSize: '12px', color: '#6B7280' }}>°</span>
            {northAngle != null && (
              <span style={{ fontSize: '12px', color: '#10B981', marginLeft: 'auto' }}>
                {angleToCardinalLabel(northAngle)}
              </span>
            )}
          </div>

          {/* Quick direction buttons */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px', marginBottom: '12px' }}>
            {QUICK_DIRECTIONS.map(({ label, angle }) => {
              const isActive = northAngle === angle;
              return (
                <button
                  key={label}
                  onClick={() => onNorthAngleChange(angle)}
                  style={{
                    padding: '5px 2px',
                    fontSize: '11px',
                    fontWeight: isActive ? 600 : 400,
                    border: `1px solid ${isActive ? '#10B981' : '#E5E7EB'}`,
                    borderRadius: '3px',
                    backgroundColor: isActive ? '#ECFDF5' : 'white',
                    color: isActive ? '#059669' : '#374151',
                    cursor: 'pointer',
                  }}
                  aria-pressed={isActive}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* View toggle */}
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => northViewActive && onNorthViewToggle()}
              style={{
                flex: 1,
                padding: '6px 4px',
                fontSize: '12px',
                border: `1px solid ${!northViewActive ? '#10B981' : '#E5E7EB'}`,
                borderRadius: '4px',
                backgroundColor: !northViewActive ? '#ECFDF5' : 'white',
                color: !northViewActive ? '#059669' : '#374151',
                fontWeight: !northViewActive ? 600 : 400,
                cursor: northViewActive ? 'pointer' : 'default',
              }}
              aria-pressed={!northViewActive}
            >
              Gezeichnet
            </button>
            <button
              onClick={() => !northViewActive && northAngle != null && onNorthViewToggle()}
              disabled={northAngle == null}
              title={northAngle == null ? 'Nordrichtung zuerst einstellen' : undefined}
              style={{
                flex: 1,
                padding: '6px 4px',
                fontSize: '12px',
                border: `1px solid ${northViewActive ? '#10B981' : '#E5E7EB'}`,
                borderRadius: '4px',
                backgroundColor: northViewActive ? '#ECFDF5' : 'white',
                color: northViewActive ? '#059669' : northAngle == null ? '#9CA3AF' : '#374151',
                fontWeight: northViewActive ? 600 : 400,
                cursor: northAngle == null ? 'not-allowed' : northViewActive ? 'default' : 'pointer',
                opacity: northAngle == null ? 0.6 : 1,
              }}
              aria-pressed={northViewActive}
              aria-disabled={northAngle == null}
              data-testid="north-view-button"
            >
              Norden oben
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompassWidget;
